import type {
  UnderwritingReportKey,
  UnderwritingRule
} from "api/underwriting-agents/types";

/** The screening reports an underwriting agent checks, in the order the rules card lists them. */
const REPORT_LABELS: Record<UnderwritingReportKey, string> = {
  basic_information: "Basic information",
  warnings_report: "Warnings report",
  identity_verification: "Identity verification",
  income_history: "Income history",
  credit_report: "Credit report",
  criminal_report: "Criminal report",
  eviction_report: "Eviction report"
};

export const getReportLabel = (key: UnderwritingReportKey) =>
  REPORT_LABELS[key];

export const UNDERWRITING_REPORTS = (
  Object.keys(REPORT_LABELS) as UnderwritingReportKey[]
).map((key) => ({ key, label: REPORT_LABELS[key] }));

// Mirror `Boom::AI::Agent::UnderwritingRule`'s limits, so a rule the API
// would refuse is never added to the page.
export const MAX_RULE_TEXT_LENGTH = 1000;
export const MAX_RULES_PER_REPORT = 50;

/**
 * A rule as a page edits it: saved on the agent (`savedId`) or added since the
 * last save. Rules are kept local until Save, so an edit of a saved rule keeps
 * its `savedId` and changes only `text`. Boom defaults (`defaultKey`) and, in a
 * template, the agent's rules (`fromAgent`) can't be edited or removed there,
 * only switched off and on; a switched-off one (`off`) stays listed, greyed.
 */
export type RuleDraft = {
  key: string;
  report_key: UnderwritingReportKey;
  text: string;
  savedId?: number;
  fromAgent?: boolean;
  defaultKey?: string;
  off?: boolean;
};

export const toRuleDraft = (rule: UnderwritingRule): RuleDraft => ({
  key: `saved-${rule.id}`,
  report_key: rule.report_key,
  text: rule.text,
  savedId: rule.id,
  defaultKey: rule.default_key ?? undefined
});

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? "" : "s"}`;

/** "11 rules across 6 reports", as the summary and the template's agent card say it. */
export const describeRules = (rules: { report_key: UnderwritingReportKey }[]) =>
  `${plural(rules.length, "rule")} across ${plural(
    new Set(rules.map((rule) => rule.report_key)).size,
    "report"
  )}`;
